import React, { MouseEventHandler } from 'react';
import { Box, Collapse } from '@mui/material';
import _ from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';

import { ChainFilter } from '@occc/graphql';
import { uuidRegExp } from '@occc-app/lib/format';
import {
  CircularProgress,
  ListItemText,
  ListItemIcon,
  ListItemButton,
} from '@occc/ui-design-system';
import { useChain, useChangeChain } from '@occc-app/Chain/hooks';
import { useGetChains } from '@occc-app/dataAccessLayer/chain/queries';
import { Chain } from '@occc-app/Chain/types';

import { BusinessIcon, ExpandLessIcon, ExpandMoreIcon } from '@occc/ui-design-system/icons';

const makeFilter = (organizationId?: string | null): ChainFilter => {
  const filter: ChainFilter = {};
  const setFilterValue = (propName: string, propValue: any) =>
    propValue ? _.set(filter, propName, propValue) : null;
  setFilterValue('organizationIdEquals', organizationId);

  return filter;
};

const ChainsDropdown = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const changeChain = useChangeChain();

  const chain = useChain();

  const [open, setOpen] = React.useState(false);
  const chainsFilter = makeFilter(chain?.attributes?.organization_id);
  const { data: chainsList } = useGetChains(chainsFilter);
  const handleClick: MouseEventHandler<HTMLElement> = () => {
    setOpen(!open);
  };

  const handleChangeChain = (chainData: Chain) => {
    changeChain(chainData);
    setOpen(false);
    const regEx = uuidRegExp({ pre: '(/[^/]+)/' });
    const match = location.pathname.match(regEx);
    if (match) {
      navigate(match[1]);
    }
  };

  const renderChain = (chainData: Chain) => {
    if (!chainData) {
      return null;
    }
    const onClick = () => handleChangeChain(chainData);

    return (
      <ListItemButton key={chainData.id} onClick={onClick}>
        <ListItemIcon>
          <BusinessIcon />
        </ListItemIcon>
        <ListItemText primary={chainData.attributes.name} />
      </ListItemButton>
    );
  };

  const renderChains = (chainsArr: Chain[]) => {
    return chainsArr.map(renderChain);
  };

  if (Array.isArray(chainsList) && chain) {
    const nonSelectedChains = chainsList
      ? chainsList.filter((thisChain) => thisChain.id !== chain.id)
      : [];

    const icon = open ? <ExpandLessIcon sx={{ ml: 1 }} /> : <ExpandMoreIcon sx={{ ml: 1 }} />;
    return (
      <Box my={1}>
        <ListItemButton onClick={handleClick}>
          <ListItemIcon>
            <BusinessIcon />
          </ListItemIcon>
          <ListItemText primary={chain.attributes.name} />
          {nonSelectedChains.length >= 1 ? icon : null}
        </ListItemButton>
        {nonSelectedChains.length >= 1 ? (
          <Collapse in={open} timeout='auto'>
            {renderChains(nonSelectedChains)}
          </Collapse>
        ) : null}
      </Box>
    );
  }

  return <CircularProgress size={24} sx={{ mx: 2, my: 1, textAlign: 'center' }} />;
};

export default ChainsDropdown;
